import React from "react";
import Header from "./header";
import {
  Box,
  Typography,
  Paper,
  Container,
  Grid,
  useTheme,
  useMediaQuery,
  Link,
} from "@mui/material";
import backgroundImg from "../assets/background.jpg";
import googleImg from "../assets/google.png";
import trustImg from "../assets/trust.png";
import yelpImg from "../assets/yelp.png";
import AccidentAssessmentForm from "../components/forms/AccidentAssessmentForm";
import Footer from "./footer";
import { useParams } from "react-router-dom";
import { extractNumber } from "../utils/helper";
import TextBox from "../components/textbox";
import { useTranslation } from "../components/hooks/useTranslation";

const HomePage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { funnelSource } = useParams<{ funnelSource?: string }>();
  const { t } = useTranslation();
  const sourceNumber = extractNumber(funnelSource);

  return (
    <>
      <Header />
      <Box
        sx={{
          backgroundImage: `linear-gradient(135deg, rgba(30, 41, 59, 0.92), rgba(51, 65, 85, 0.92)), url(${backgroundImg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
          width: "100%",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          position: "relative",
          padding: "2rem 0",
        }}
      >
        <Container maxWidth="lg">
          {/* Main Heading */}
          {sourceNumber === 2 && (
            <>
              <Typography
                variant={isMobile ? "h5" : "h4"}
                align="center"
                sx={{
                  color: "white",
                  fontWeight: 700,
                  textShadow: "2px 2px 8px rgba(0, 0, 0, 0.8)",
                  mt: 2,
                  mb: 2,
                  fontSize: {
                    xs: "1.5rem",
                    sm: "1.8rem",
                    md: "2.5rem",
                  },
                  lineHeight: 1.3,
                }}
              >
                {t("homePageYT.firstTitle")}
                <br />
                <span
                  style={{
                    color: "#fbbf24",
                    textDecoration: "underline",
                    textDecorationColor: "#fbbf24",
                    textDecorationThickness: "3px",
                    textUnderlineOffset: "6px",
                  }}
                >
                  {t("homePageYT.secondTitle")}
                </span>
              </Typography>
              <Typography
                variant="h6"
                align="center"
                sx={{
                  color: "#e5e7eb",
                  textShadow: "1px 1px 4px rgba(0, 0, 0, 0.8)",
                  mb: isMobile ? 2 : 3,
                  mt: isMobile ? 2 : 3,
                  fontSize: {
                    xs: "1rem",
                    sm: "1.1rem",
                    md: "1.4rem",
                  },
                  lineHeight: 1.6,
                }}
              >
                {t("homePageYT.firstSubtitle")}{" "}
                <span style={{ color: "#fb923c", fontWeight: 700 }}>
                  {t("homePageYT.secondSubtitle")}
                </span>{" "}
                <span>{t("homePageYT.thirdSubtitle")}</span>
              </Typography>
            </>
          )}
          {sourceNumber !== 2 && (
            <>
              <Typography
                variant={isMobile ? "h5" : "h4"}
                align="center"
                sx={{
                  color: "white",
                  fontWeight: 700,
                  textShadow: "2px 2px 8px rgba(0, 0, 0, 0.8)",
                  mt: 2,
                  fontSize: {
                    xs: "1.5rem",
                    sm: "1.8rem",
                    md: "2.5rem",
                  },
                  lineHeight: 1.3,
                }}
              >
                {t("homePageFB.firstTitle")}{" "}
                <span
                  style={{
                    color: "#fbbf24",
                    textDecoration: "underline",
                    textDecorationColor: "#fbbf24",
                    textDecorationThickness: "3px",
                    textUnderlineOffset: "6px",
                  }}
                >
                  {t("homePageFB.secondTitle")}
                </span>{" "}
                {t("homePageFB.thirdTitle")}
              </Typography>

              <Typography
                variant={isMobile ? "h6" : "h4"}
                align="center"
                sx={{
                  color: "white",
                  fontWeight: 700,
                  textShadow: "2px 2px 8px rgba(0, 0, 0, 0.8)",
                  mb: isMobile ? 2 : 2,
                  mt: 1,
                  fontSize: {
                    xs: "1.5rem",
                    sm: "1.8rem",
                    md: "2.5rem",
                  },
                  lineHeight: 1.3,
                }}
              >
                {t("homePageFB.fourthTitle")}
              </Typography>

              <Typography
                variant="h6"
                align="center"
                sx={{
                  color: "#e5e7eb",
                  textShadow: "1px 1px 4px rgba(0, 0, 0, 0.8)",
                  mb: isMobile ? 2 : 3,
                  fontSize: {
                    xs: "1rem",
                    sm: "1.1rem",
                    md: "1.4rem",
                  },
                  lineHeight: 1.6,
                }}
              >
                {t("homePageFB.firstSubtitle")}{" "}
                <span style={{ color: "#fb923c", fontWeight: 700 }}>
                  {t("homePageFB.secondSubtitle")}
                </span>{" "}
                {t("homePageFB.thirdSubtitle")}{" "}
                <span style={{ color: "#fb923c", fontWeight: 700 }}>
                  {t("homePageFB.fourthSubtitle")}
                </span>
              </Typography>
            </>
          )}

          {sourceNumber === 3 && <TextBox />}

          {/* Assessment Form */}
          {sourceNumber !== 6 && (
            <Paper
              elevation={6}
              sx={{
                pt: 3,
                pb: 4,
                pl: { xs: 2, md: 4 },
                pr: { xs: 2, md: 4 },
                borderRadius: 3,
                mb: 4,
                width: "100%",
                maxWidth: "800px",
                mx: "auto",
                boxShadow: "0 10px 40px rgba(0, 0, 0, 0.5)",
              }}
            >
              <AccidentAssessmentForm sourceNumber={sourceNumber ?? 0} />
            </Paper>
          )}

          {sourceNumber === 6 && (
            <Paper
              elevation={6}
              sx={{
                pt: 3,
                pb: 4,
                pl: { xs: 2, md: 4 },
                pr: { xs: 2, md: 4 },
                borderRadius: 3,
                mb: 4,
                width: "100%",
                maxWidth: "800px",
                mx: "auto",
                boxShadow: "0 10px 40px rgba(0, 0, 0, 0.5)",
              }}
            >
              <AccidentAssessmentForm
                sourceNumber={6}
                skipSteps={["INJURY", "REPRESENTATION", "FAULT"]}
                defaultValues={{
                  physicalInjury: "YES",
                  treatedBy: "YES",
                  hasAttorney: "NO",
                  accidentFault: "NO",
                }}
              />
            </Paper>
          )}

          {/* Free Consultation Box */}
          <Box
            sx={{
              backgroundColor: "rgba(34, 197, 94, 0.15)",
              border: "2px solid #22c55e",
              borderRadius: 2,
              padding: "1rem 1.5rem",
              mb: 4,
              maxWidth: "800px",
              mx: "auto",
            }}
          >
            <Typography
              variant="h6"
              align="center"
              sx={{
                color: "white",
                fontWeight: 600,
                textShadow: "1px 1px 3px rgba(0, 0, 0, 0.8)",
              }}
            >
              {t("homepage.text")}
            </Typography>
          </Box>

          {/* Rating Text */}
          <Typography
            variant="h5"
            align="center"
            sx={{
              color: "white",
              fontWeight: 700,
              textShadow: "2px 2px 6px rgba(0, 0, 0, 0.8)",
              mb: 3,
              fontSize: {
                xs: "1.25rem",
                sm: "1.5rem",
                md: "1.75rem",
              },
            }}
          >
            {t("homepage.text2")}
          </Typography>

          {/* Review Cards */}
          <Grid
            container
            spacing={2}
            justifyContent="center"
            sx={{
              mb: 4,
              maxWidth: "700px",
              mx: "auto",
            }}
          >
            <Grid item xs={12} sm={4}>
              <Box
                sx={{
                  backgroundColor: "white",
                  borderRadius: 3,
                  padding: "1.5rem",
                  textAlign: "center",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.4)",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "1.5rem",
                    fontWeight: 700,
                    color: "#d32323",
                  }}
                >
                  Yelp
                </Typography>
                <Box
                  sx={{ color: "#fbbf24", fontSize: "1.5rem", lineHeight: 1 }}
                >
                  ★★★★★
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Box
                sx={{
                  backgroundColor: "white",
                  borderRadius: 3,
                  padding: "1.5rem",
                  textAlign: "center",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.4)",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "1.5rem",
                    fontWeight: 700,
                    color: "#4285f4",
                  }}
                >
                  Google
                </Typography>
                <Box
                  sx={{ color: "#fbbf24", fontSize: "1.5rem", lineHeight: 1 }}
                >
                  ★★★★★
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Box
                sx={{
                  backgroundColor: "white",
                  borderRadius: 3,
                  padding: "1.5rem",
                  textAlign: "center",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.4)",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "1.5rem",
                    fontWeight: 700,
                    color: "#00b67a",
                  }}
                >
                  Trustpilot
                </Typography>
                <Box
                  sx={{ color: "#fbbf24", fontSize: "1.5rem", lineHeight: 1 }}
                >
                  ★★★★★
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
      <Container sx={{ mt: "2%", mb: "2%" }} maxWidth="md">
        <Typography sx={{ mb: "10px", textAlign: "center" }} variant="body2">
          {t("homepage.tcpa1")}{" "}
          <Link
            href="https://www.caraccidentcounsel.com/terms-of-service/"
            sx={{ color: "#3b82f6", fontWeight: 500 }}
          >
            {t("homepage.tcpa2")}
          </Link>{" "}
          {t("homepage.tcpa3")}{" "}
          <Link
            href="https://www.caraccidentcounsel.com/network-of-lawyers-and-advocates/"
            sx={{ color: "#3b82f6", fontWeight: 500 }}
          >
            {t("homepage.tcpa4")}
          </Link>{" "}
          {t("homepage.tcpa5")}
        </Typography>
        <Typography sx={{ mb: "10px", textAlign: "center" }} variant="body2">
          {t("homepage.tcpa6")}
        </Typography>
        <Typography sx={{ mb: "10px", textAlign: "center" }} variant="body2">
          {t("homepage.tcpa7")}
        </Typography>
        <Typography sx={{ mb: "10px", textAlign: "center" }} variant="body2">
          {t("homepage.tcpa8")}{" "}
          <Link
            href="https://www.caraccidentcounsel.com/sponsors/"
            sx={{ color: "#3b82f6", fontWeight: 500 }}
          >
            {t("homepage.tcpa9")}
          </Link>{" "}
          {t("homepage.tcpa10")}
        </Typography>
      </Container>
      <Footer />
    </>
  );
};

export default HomePage;
